import type { SymbolDataStructure } from '@tsagko/label-contracts'
import { useTranslation } from 'react-i18next'
import {
  AI_OPTIONS,
  aiInfoFor,
  checkDigitHint,
  suggestedVariableName,
  type HintDescriptor,
} from './symbol-config'
import { ValueField } from './ValueField'

type Gs1Element = Extract<SymbolDataStructure, { kind: 'gs1-ai' }>['elements'][number]

interface Props {
  elements: Gs1Element[]
  onChange: (elements: Gs1Element[]) => void
}

/**
 * A hint for AIs whose data embeds a check digit (GTIN / SSCC). Returns null
 * for AIs with no check digit, for {variable} values, and for too-short input.
 * The hint is informational only — the operator supplies the full GTIN/SSCC
 * (including its check digit); we validate what they typed.
 *
 * IMPORTANT: uses `checkDigit === 'none'` (NOT `!== 'gtin'`) so that AI `00`
 * (SSCC, checkDigit:'sscc') also shows a hint.
 *
 * Returns a {key, params} descriptor, not finished prose — a free function
 * has no `t`, so the component translates it at render.
 */
function gtinHint(ai: string, content: string): HintDescriptor | null {
  if (aiInfoFor(ai).checkDigit === 'none') return null
  const digits = content.replace(/\D/g, '')
  if (content.includes('{') || digits.length < 2) return null
  return checkDigitHint(digits)
}

export function Gs1AiEditor({ elements, onChange }: Props) {
  const { t } = useTranslation()
  const setAt = (i: number, next: Gs1Element) =>
    onChange(elements.map((el, j) => (j === i ? next : el)))
  const isKnown = (ai: string) => AI_OPTIONS.some(o => o.ai !== 'custom' && o.ai === ai)

  return (
    <div className="space-y-3">
      {elements.map((el, i) => {
        const usingCustom = !isKnown(el.ai)
        const hint = gtinHint(el.ai, el.value.content)
        // key by index: gs1-ai elements carry no stable id (schema is {ai,value}).
        // ValueField is controlled by `content`, so displayed values stay correct on
        // add/remove; the only cost is a possible input-focus shift when a middle row
        // is removed — acceptable for this small editor.
        return (
          <div key={i} data-testid="gs1-row" className="rounded border border-stone-200 p-2 dark:border-stone-700">
            <div className="flex items-center gap-1">
              <select
                data-testid="gs1-ai-select"
                value={usingCustom ? 'custom' : el.ai}
                onChange={e => {
                  const v = e.target.value
                  setAt(i, { ...el, ai: v === 'custom' ? '' : v })
                }}
                className="flex-1 rounded border border-stone-300 px-1 py-0.5 text-sm dark:border-stone-700 dark:bg-stone-950"
              >
                {AI_OPTIONS.map(o => (
                  <option key={o.ai} value={o.ai}>{t(o.labelKey)}</option>
                ))}
              </select>
              <button
                type="button"
                data-testid="gs1-remove"
                aria-label={t('symbology.removeElement')}
                onClick={() => onChange(elements.filter((_, j) => j !== i))}
                className="px-2 text-stone-400 hover:text-red-500"
              >✕</button>
            </div>
            {usingCustom && (
              <input
                data-testid="gs1-ai-custom"
                value={el.ai}
                onChange={e => setAt(i, { ...el, ai: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                placeholder={t('symbology.customAiPlaceholder')}
                className="mt-1 w-full rounded border border-stone-300 px-2 py-1 font-mono text-sm dark:border-stone-700 dark:bg-stone-950"
              />
            )}
            <div className="mt-1">
              <ValueField
                content={el.value.content}
                suggestedName={suggestedVariableName(el.ai)}
                onChange={content => setAt(i, { ...el, value: { content } })}
              />
              {hint && (
                <p data-testid="gs1-hint" className="mt-1 text-[11px] text-stone-500">
                  {t(hint.key, hint.params)}
                </p>
              )}
            </div>
          </div>
        )
      })}
      <button
        type="button"
        data-testid="gs1-add"
        onClick={() => onChange([...elements, { ai: '01', value: { content: '' } }])}
        className="text-xs text-sky-600 hover:underline"
      >{t('symbology.addElement')}</button>
    </div>
  )
}
